// @ts-strict-ignore
import Icon from '@ant-design/icons';
import Skeleton from 'antd/lib/skeleton';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../timeHelpers';
import {
    OrgOverviewStatistic,
    ReleaseStepDurations,
    StatisticSampleBucketDataPoint,
    StatisticScope,
    StatisticTimeSeriesDataPoint,
} from '../../types';
import { antGrey } from '../styles/globalStyles';
import { useChartColors } from '../styles/themeMode';
import {
    colorForStatCategory,
    displayOrgOverviewStatisticLabel,
    iconForStatCategory,
    tooltipForScope,
} from './orgOverviewHelpers';
import { displayTextForReleaseStepType } from '../../helpers/displayHelpers';
import { BarGraphTimeBucketDataPoint } from '../../typescriptHelpers';
import { flatMap, isNil } from 'lodash';
import { OrgOverviewStackedBarGraph } from './OrgOverviewRecharts';
import { StatSection } from './OrgOverviewStatCards';

interface OrgOverviewReleaseStepDurationsV2Props extends OrgOverviewStatistic {
    isLoading?: boolean;
    scope: StatisticScope;
    isWorkflowsCIStepEnabled: boolean;
}

// Hiding any steps with duration under 1 minute to prevent rendering bad/unuseful data
const MIN_VISIBLE_STEP_DURATION_SECONDS = 60;

export const OrgOverviewReleaseStepDurationsV2: React.FC<OrgOverviewReleaseStepDurationsV2Props> = ({
    type,
    category,
    displayText,
    description,
    unit,
    dataPoints,
    isLoading,
    scope,
    isWorkflowsCIStepEnabled,
}) => {
    const chartColors = useChartColors();
    const title = displayText || displayOrgOverviewStatisticLabel(type);
    const tooltip = description || tooltipForScope(scope, type);
    const typedDataPoints = dataPoints as Array<StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint>;

    const data: BarGraphTimeBucketDataPoint[] = flatMap(typedDataPoints, (dataPoint) => {
        const releaseStepDurations = dataPoint.value as ReleaseStepDurations;
        if (isNil(releaseStepDurations) || releaseStepDurations.totalDuration <= 0) {
            return [];
        }

        const timeBucket = getReleaseStepDurationTimeBucketKey(dataPoint);
        const axisLabel = getReleaseStepDurationAxisLabel(dataPoint);
        const tooltipTitle = getReleaseStepDurationTooltipTitle(dataPoint);
        return releaseStepDurations.durations.map(({ releaseStepType, duration }) => ({
            timeBucket,
            axisLabel,
            tooltipTitle,
            type: displayTextForReleaseStepType(releaseStepType, null, null, isWorkflowsCIStepEnabled),
            value: duration > MIN_VISIBLE_STEP_DURATION_SECONDS ? duration : null,
            total: releaseStepDurations.totalDuration,
        }));
    });

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                alignItems: 'stretch',
            }}
        >
            {isLoading ?
                <Skeleton active round={false} style={{ padding: 12 }} paragraph={{ rows: 3 }} />
            :   <>
                    <div style={{ height: 56, flexShrink: 0 }}>
                        <StatSection
                            title={title}
                            tooltip={tooltip}
                            cardIcon={
                                <Icon
                                    component={iconForStatCategory(category)}
                                    style={{
                                        fontSize: 14,
                                        color: colorForStatCategory(category),
                                    }}
                                />
                            }
                        />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 200,
                            backgroundColor: 'var(--rw-chart-container-bg)',
                        }}
                    >
                        {data.length ?
                            <OrgOverviewStackedBarGraph
                                data={getRechartsBarGraphData(data)}
                                type={type}
                                unit={unit}
                                chartColors={chartColors}
                                showLegend={false}
                                hideYAxis
                                hideXAxis={false}
                                appendPadding={[12, 20, 28, 20]}
                            />
                        :   <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <Text
                                    style={{
                                        color: antGrey.grey6,
                                        width: 162,
                                        marginLeft: 20,
                                    }}
                                >
                                    Waiting for more release data.
                                </Text>
                            </div>
                        }
                    </div>
                </>
            }
        </div>
    );
};

function getReleaseStepDurationTimeBucketLabel(
    dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint,
) {
    const bucketLabel = getTimeBucketLabel(dataPoint);

    if ('timestamp' in dataPoint && dataPoint.releaseId) {
        return `${dataPoint.releaseId} (${bucketLabel})`;
    }

    return bucketLabel;
}

function getReleaseStepDurationTimeBucketKey(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint) {
        return getReleaseKey(dataPoint);
    }

    return `${dataPoint.sampleStartedAt}|${dataPoint.sampleEndedAt}`;
}

function getReleaseStepDurationAxisLabel(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint) {
        return formatCalendarDateLabel(dataPoint.timestamp);
    }

    return getTimeBucketLabel(dataPoint);
}

function getReleaseStepDurationTooltipTitle(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint && dataPoint.releaseId) {
        return dataPoint.releaseId;
    }

    return getReleaseStepDurationTimeBucketLabel(dataPoint);
}

function getReleaseKey(dataPoint: StatisticTimeSeriesDataPoint) {
    return dataPoint.releaseId ?? dataPoint.timestamp;
}

function getTimeBucketLabel(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint) {
        return formatCalendarDateLabel(dataPoint.timestamp);
    }

    return `${formatCalendarDateLabel(dataPoint.sampleStartedAt)} - ${formatCalendarDateLabel(
        dataPoint.sampleEndedAt,
    )}`;
}

function formatCalendarDateLabel(value: string) {
    const format = DatetimeFormat.ShortMonthDateYear;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return dayjs(value).format(format);
    }

    return dayjs.utc(value).local().format(format);
}

function getRechartsBarGraphData(data: BarGraphTimeBucketDataPoint[]) {
    if (isNil(data) || data.length < 1) {
        return null;
    }

    return data;
}
